import { classes, icons, useCopyToClipboard } from '@portal/components'
import React, { useState } from 'react'

const { Check, Copy, Eye, EyeCross } = icons

export const MASKED_VALUE = Array(40).fill('*').join('')

type CustomRenderProps = {
  mask?: never
  /** Render prop to override the default render */
  render: () => React.ReactElement | React.ReactElement[]
}

type WithoutCustomRenderProps = {
  /**
   * Optional flag to mask the value. This **WILL NOT** work when providing
   * a custom render prop.
   */
  mask?: boolean
  render?: never
}

type Props = {
  /** The value to be copied */
  value: string
  /** The title of the entry */
  title?: string
  /** Optional test id for the value's container */
  testId?: string
  /** Optional flag to render the value with mono font */
  isCode?: boolean
  /** Optional callback that can get triggered after successful copy */
  onCopied?: () => void
  /** Optional flag to render the entire entry width */
  fullWidth?: boolean
} & (CustomRenderProps | WithoutCustomRenderProps)

// Renders a value, with a title, that can be copied to the clipboard.
const CopyableEntry = ({
  testId,
  value,
  mask,
  title,
  isCode,
  onCopied,
  render,
  fullWidth,
}: Props) => {
  const [isMasked, setIsMasked] = useState(mask ?? false)

  const [doCopy, recentlyCopied, canCopy] = useCopyToClipboard(
    value || '',
    2000,
  )

  const handleCopy = async () => {
    await doCopy()
    onCopied?.()
  }

  const VisibilityIcon = isMasked ? Eye : EyeCross

  const renderValue = () => {
    if (render) {
      return render()
    } else if (isMasked) {
      return MASKED_VALUE
    } else {
      return value
    }
  }

  return (
    <div className={classes({ 'w-full': fullWidth })}>
      {!!title && (
        <label
          htmlFor="about"
          className="block text-base font-medium text-foreground-normal mb-1"
        >
          {title}
        </label>
      )}
      <div className="flex items-start space-x-4">
        <div
          className={classes('flex-grow break-all text-foreground-subtle', {
            'font-plex-mono': isCode,
          })}
          data-testid={testId}
        >
          {renderValue()}
        </div>
        {!!value && (
          <div className={classes('flex flex-row', { 'gap-x-2': mask })}>
            {mask && (
              <button
                onClick={() => setIsMasked(!isMasked)}
                className="ml-1 outline-none focus-visible:outline-2 focus-visible:outline-focus-outline"
                data-testid="visibilityButton"
              >
                <VisibilityIcon className="h-4 w-4 text-foreground-subtle" />
              </button>
            )}

            {canCopy && (
              <button
                onClick={handleCopy}
                className="ml-1 outline-none focus-visible:outline-2 focus-visible:outline-focus-outline"
                data-testid="copyButton"
              >
                {recentlyCopied ? (
                  <Check
                    className="h-4 w-4 text-fill-success"
                    data-testid="recentlyCopiedIcon"
                  />
                ) : (
                  <Copy
                    className="h-4 w-4 text-foreground-subtle hover:text-foreground-normal group-focus-visible:text-foreground-normal"
                    data-testid="copyIcon"
                  />
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default CopyableEntry
